import { c as _c } from "react/compiler-runtime";
import { Chips, NumberInput, Separator, useTranslationContext } from 'platform/components';
import { HStack, VStack } from 'platform/foundation-tw';

// eslint-disable-next-line no-restricted-imports -- Intended
import { isNil, isNotNil, path } from 'ramda';
import { isString, toNumber } from 'ramda-adjunct';
import { suffixTestId } from 'shared/web';
import { useFilterOnChipsChange } from '../../hooks/useFilterOnChipsChange';
import type { EagFilterProps } from '../../types/EagFilterProps';
export type RangeFilterType = EagFilterProps & {
  quickFilters?: Array<{
    value: string;
    label: string;
  }>;
};
type FilterValue = {
  from: number | null;
  to: number | null;
} | string;
const FILTER_VALUE_KEY = 'value';
export function RangeFilter(props) {
  const $ = _c(49);
  const t = useTranslationContext();
  const filterValue = props.value[FILTER_VALUE_KEY] as FilterValue | null;
  let t0;
  if ($[0] !== props) {
    t0 = value => {
      props.onChange({
        [FILTER_VALUE_KEY]: value
      });
    };
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const setFilterValue = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      from: null,
      to: null
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== setFilterValue) {
    t2 = {
      setFilterValue,
      defaultValue: t1
    };
    $[3] = setFilterValue;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    onChipsChange
  } = useFilterOnChipsChange(t2);
  let t3;
  if ($[5] !== filterValue || $[6] !== props || $[7] !== setFilterValue) {
    t3 = value_0 => {
      if (typeof filterValue !== "string" && isNil(value_0) && isNil((filterValue as {
        to: number | null;
      } | null)?.to)) {
        props.onChange({
          [FILTER_VALUE_KEY]: null
        });
        return;
      }
      if (isString(filterValue) || isNil(filterValue)) {
        setFilterValue({
          from: toNumber(value_0),
          to: null
        });
      }
      if (!isString(filterValue)) {
        setFilterValue({
          from: isNil(value_0) ? value_0 : toNumber(value_0),
          to: (filterValue as {
            to: number | null;
          } | null)?.to === 0 ? null : (filterValue as {
            to: number | null;
          } | null)?.to ?? null
        });
      }
    };
    $[5] = filterValue;
    $[6] = props;
    $[7] = setFilterValue;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const onFromInputChange = t3;
  let t4;
  if ($[9] !== filterValue || $[10] !== props || $[11] !== setFilterValue) {
    t4 = value_1 => {
      const fromValue = (filterValue as {
        from: number | null;
      } | null)?.from;
      if (typeof filterValue !== "string" && isNil(value_1) && (isNil(fromValue) || fromValue === 0)) {
        props.onChange({
          [FILTER_VALUE_KEY]: null
        });
        return;
      }
      if (isString(filterValue) || isNil(filterValue)) {
        setFilterValue({
          to: toNumber(value_1),
          from: null
        });
      }
      if (!isString(filterValue)) {
        setFilterValue({
          from: fromValue === 0 ? null : fromValue ?? null,
          to: isNil(value_1) ? value_1 : toNumber(value_1)
        });
      }
    };
    $[9] = filterValue;
    $[10] = props;
    $[11] = setFilterValue;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const onToInputChange = t4;
  let t5;
  if ($[13] !== filterValue) {
    t5 = isString(filterValue) ? [filterValue] : null;
    $[13] = filterValue;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const isQuickFilterValue = t5;
  let t6;
  if ($[15] !== isQuickFilterValue || $[16] !== onChipsChange || $[17] !== props) {
    t6 = props.quickFilters && props.quickFilters.length > 0 && <><HStack><Chips isDisabled={props.isDisabled} value={isQuickFilterValue} options={props.quickFilters} onChange={onChipsChange} isMultiple={false} data-testid={suffixTestId("range-filter-quickFilters", props)} isDeselectable={true} /></HStack><Separator /></>;
    $[15] = isQuickFilterValue;
    $[16] = onChipsChange;
    $[17] = props;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t) {
    t7 = t("general.labels.from");
    $[19] = t;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== filterValue) {
    t8 = hasValidKey(filterValue, "from") ? toNumber(filterValue.from) : null;
    $[21] = filterValue;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const t9 = props.isDisabled;
  let t10;
  if ($[23] !== props) {
    t10 = suffixTestId("range-filter-from", props);
    $[23] = props;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== onFromInputChange || $[26] !== props.isDisabled || $[27] !== t10 || $[28] !== t7 || $[29] !== t8) {
    t11 = <NumberInput label={t7} value={t8} onChange={onFromInputChange} isDisabled={t9} data-testid={t10} />;
    $[25] = onFromInputChange;
    $[26] = props.isDisabled;
    $[27] = t10;
    $[28] = t7;
    $[29] = t8;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t) {
    t12 = t("general.labels.to");
    $[31] = t;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== filterValue) {
    t13 = hasValidKey(filterValue, "to") ? toNumber(filterValue.to) : null;
    $[33] = filterValue;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const t14 = props.isDisabled;
  let t15;
  if ($[35] !== props) {
    t15 = suffixTestId("range-filter-to", props);
    $[35] = props;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== onToInputChange || $[38] !== props.isDisabled || $[39] !== t12 || $[40] !== t13 || $[41] !== t15) {
    t16 = <NumberInput label={t12} value={t13} onChange={onToInputChange} isDisabled={t14} data-testid={t15} />;
    $[37] = onToInputChange;
    $[38] = props.isDisabled;
    $[39] = t12;
    $[40] = t13;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t11 || $[44] !== t16) {
    t17 = <HStack spacing={5}>{t11}{t16}</HStack>;
    $[43] = t11;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t17 || $[47] !== t6) {
    t18 = <VStack>{t6}{t17}</VStack>;
    $[46] = t17;
    $[47] = t6;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  return t18;
}
const hasValidKey = <T extends 'from' | 'to',>(val: unknown, key: T): val is Record<T, string> => isNotNil(path([key], val));